/**
 * Screen Share Service
 *
 * Runs two consumers off a single `getDisplayMedia` stream:
 *
 * 1. **Vision channel (to Gemini, 1fps):**
 *    Every second a frame is drawn to a canvas, encoded as JPEG,
 *    and sent over the WebSocket as a binary message of type 0x03.
 *    Worker forwards it to Gemini as a realtimeInput image/jpeg chunk.
 *
 * 2. **Recording channel (to R2, 2s chunks):**
 *    A MediaRecorder emits WebM/MP4 segments, wrapped in binary type 0x04
 *    messages (same framing as camera 0x02). Worker uploads them to R2
 *    as `segment_kind: 'screen'` segments alongside the camera recording.
 *
 * Both channels reuse the live-session WebSocket - no new connection.
 *
 * Lifecycle: `start()` → streaming → `stop()`. `stop()` is idempotent.
 * Browser-level "Stop sharing" (track.onended) is detected and surfaced
 * via `onStopped({ reason: 'browser_stopped' })`. A 180s hard cap also
 * fires `onStopped({ reason: 'duration_cap' })` to bound token cost.
 */

import { captureException } from '../lib/telemetry/posthog/frontend';

/** Binary message type for screen-share JPEG frames. Must match worker `SCREEN_FRAME_MESSAGE_TYPE`. */
const SCREEN_FRAME_MESSAGE_TYPE = 0x03;

/** Binary message type for screen-share recording chunks. Must match worker `SCREEN_VIDEO_MESSAGE_TYPE`. */
const SCREEN_VIDEO_MESSAGE_TYPE = 0x04;

/** Target frame sampling rate sent to Gemini. Low to contain cost. */
const FRAME_FPS = 1;

/** JPEG encode quality (0..1). Low-ish - Gemini doesn't need high fidelity. */
const FRAME_JPEG_QUALITY = 0.6;

/** Max dimensions for sampled frames. Larger is rescaled. */
const FRAME_MAX_WIDTH = 1280;
const FRAME_MAX_HEIGHT = 720;

/** MediaRecorder chunk interval - matches camera recorder for consistency. */
const RECORDING_CHUNK_INTERVAL_MS = 2000;

/** Video bitrate for the recording (1 Mbps - same as camera recording). */
const RECORDING_BITRATE = 1_000_000;

/** Hard safety cap in ms. Belt-and-braces against the worker cap. */
const DURATION_CAP_MS = 180_000;

/** Reasons the share can end. Must be a subset of `ClientScreenShareStoppedMessage.reason` on the worker. */
export type ScreenShareStopReason =
  | 'user_stopped'
  | 'ai_ended'
  | 'duration_cap'
  | 'browser_stopped'
  | 'error'
  | 'session_ending';

export interface ScreenShareStartConfig {
  websocket: WebSocket;
  onStopped: (reason: ScreenShareStopReason) => void;
  onError: (error: Error) => void;
  /** Fires once the MediaStream is acquired (useful for UI state). */
  onStarted?: (stream: MediaStream) => void;
  /**
   * Returns the MediaStream whose audio track should be mixed into the
   * screen-share recording (typically the call's recording destination,
   * which already blends user mic + AI playback). The recording stream
   * shares the *same* track object - we do NOT clone it. The caller's track
   * is a `MediaStreamAudioDestinationNode` output: cloning it and later
   * calling `stop()` on the clone disrupts the underlying sink in Chrome,
   * which silently kills the camera `MediaRecorder`'s audio input (the
   * camera recorder is using the exact same source track). Must be a live
   * getter - the pipeline may swap devices mid-share.
   */
  getAudioStream?: () => MediaStream | null;
}

export class ScreenShareService {
  private stream: MediaStream | null = null;
  private recordingStream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private frameCanvas: HTMLCanvasElement | null = null;
  private frameCtx: CanvasRenderingContext2D | null = null;
  private frameVideo: HTMLVideoElement | null = null;
  private frameInterval: ReturnType<typeof setInterval> | null = null;
  private durationTimer: ReturnType<typeof setTimeout> | null = null;
  private recordingChunkIndex = 0;
  private config: ScreenShareStartConfig | null = null;
  private active = false;
  private frameInFlight = false;
  private actualMimeType = '';

  /** Codec preference - same order as videoRecorderService for consistency. */
  private static readonly MIME_TYPES = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4;codecs=avc1,mp4a.40.2',
    'video/mp4;codecs=h264,aac',
    'video/mp4',
  ];

  /**
   * Whether the current platform can actually acquire a display-media stream.
   *
   * Method-existence alone is not enough: Chrome on Android exposes
   * `getDisplayMedia` but always rejects it (crbug 487935, unresolved since
   * 2015) and iPadOS Safari reports its UA as "Mac" - only
   * `navigator.maxTouchPoints > 1` distinguishes a tablet from a desktop.
   *
   * Worker ANDs the campaign DB flag with this client capability before
   * declaring screen-share tools to Gemini (see handleInterviewContext).
   */
  static isSupported(): boolean {
    if (typeof navigator === 'undefined') return false;
    if (!navigator.mediaDevices) return false;
    if (typeof navigator.mediaDevices.getDisplayMedia !== 'function') return false;
    if (typeof MediaRecorder === 'undefined') return false;

    const ua = navigator.userAgent;
    // iOS: method is undefined across all WebKit iOS browsers. iPadOS reports
    // as Mac - catch via touchpoints so we don't false-positive tablets.
    const isIOS = /iPad|iPhone|iPod/.test(navigator.platform)
      || (ua.includes('Mac') && navigator.maxTouchPoints > 1);
    if (isIOS) return false;

    // Android: Chrome's method exists but always rejects (crbug 487935);
    // Firefox / Samsung Internet same. Exclude all Android.
    if (/Android/.test(ua)) return false;

    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  /**
   * Start sharing. Throws on getDisplayMedia failure so the caller can surface
   * a user-friendly message.
   */
  async start(config: ScreenShareStartConfig): Promise<void> {
    if (this.active) {
      console.warn('[ScreenShare] start() called while already active');
      return;
    }
    if (!ScreenShareService.isSupported()) {
      throw new Error('Screen sharing is not supported in this browser');
    }

    this.config = config;
    this.recordingChunkIndex = 0;
    this.frameInFlight = false;

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        frameRate: { ideal: 10, max: 15 },
        width: { max: FRAME_MAX_WIDTH },
        height: { max: FRAME_MAX_HEIGHT },
      },
      audio: false,
    });
    this.stream = stream;
    this.active = true;

    // Detect browser-level "Stop sharing" by watching the video track.
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.addEventListener('ended', () => {
        if (this.active) this.stopInternal('browser_stopped');
      });
    }

    this.startFrameLoop(stream);

    // Build a composite stream: screen video + the call's audio track
    // by reference (NOT a clone). The audio track is the output of a
    // MediaStreamAudioDestinationNode that the camera `MediaRecorder` is
    // also consuming as its audio input. Sharing the reference means when
    // our MediaRecorder stops and we drop this stream, nothing stops the
    // track - the camera recorder keeps going. Cloning + stopping the
    // clone on teardown disrupts the underlying sink in Chrome and kills
    // the camera recorder's audio after a ~9s buffer drain (regression seen
    // on session 2a507f77-f0f6-4ddf-98e9-060a867ff041). Falls back to
    // video-only when no audio stream is available.
    const recordingStream = new MediaStream();
    stream.getVideoTracks().forEach((t) => recordingStream.addTrack(t));
    const audioStream = config.getAudioStream?.();
    const audioTrack = audioStream?.getAudioTracks()[0];
    if (audioTrack) {
      recordingStream.addTrack(audioTrack);
    }
    this.recordingStream = recordingStream;
    this.startRecording(recordingStream, config.websocket);

    this.durationTimer = setTimeout(() => {
      if (this.active) this.stopInternal('duration_cap');
    }, DURATION_CAP_MS);

    config.onStarted?.(stream);
  }

  /** User- or caller-requested stop. Public side calls this; internal fall-through uses stopInternal. */
  stop(reason: ScreenShareStopReason = 'user_stopped'): void {
    this.stopInternal(reason);
  }

  private stopInternal(reason: ScreenShareStopReason): void {
    if (!this.active) return;
    this.active = false;

    if (this.durationTimer) {
      clearTimeout(this.durationTimer);
      this.durationTimer = null;
    }

    if (this.frameInterval) {
      clearInterval(this.frameInterval);
      this.frameInterval = null;
    }

    if (this.recorder && this.recorder.state !== 'inactive') {
      try {
        this.recorder.requestData();
        this.recorder.stop();
      } catch (err) {
        console.warn('[ScreenShare] Recorder stop failed:', err);
      }
    }
    this.recorder = null;

    // Drop the recording-stream reference but never stop its tracks: the mic
    // audio track belongs to the call's audio pipeline / camera recorder,
    // and the screen video track is already in `this.stream` below.
    this.recordingStream = null;

    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }

    if (this.frameVideo) {
      this.frameVideo.srcObject = null;
      this.frameVideo = null;
    }
    this.frameCanvas = null;
    this.frameCtx = null;

    const cb = this.config?.onStopped;
    this.config = null;
    cb?.(reason);
  }

  // ──────────── vision channel (1 fps JPEG → binary 0x03) ────────────

  private startFrameLoop(stream: MediaStream): void {
    const video = document.createElement('video');
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    this.frameVideo = video;

    // We don't attach the video element to the DOM; play() starts decoding.
    video.play().catch((err) => {
      console.warn('[ScreenShare] Hidden video play() rejected:', err);
    });

    this.frameCanvas = document.createElement('canvas');
    this.frameCtx = this.frameCanvas.getContext('2d');

    const tick = () => {
      if (!this.active) return;
      this.sampleAndSendFrame().catch((err) => {
        console.warn('[ScreenShare] frame tick failed:', err);
      });
    };

    this.frameInterval = setInterval(tick, 1000 / FRAME_FPS);
  }

  private async sampleAndSendFrame(): Promise<void> {
    if (this.frameInFlight) return;
    const video = this.frameVideo;
    const canvas = this.frameCanvas;
    const ctx = this.frameCtx;
    const ws = this.config?.websocket;
    if (!video || !canvas || !ctx || !ws) return;
    if (video.readyState < 2) return; // not enough data yet
    if (ws.readyState !== WebSocket.OPEN) return;

    const srcWidth = video.videoWidth;
    const srcHeight = video.videoHeight;
    if (!srcWidth || !srcHeight) return;

    const scale = Math.min(1, FRAME_MAX_WIDTH / srcWidth, FRAME_MAX_HEIGHT / srcHeight);
    const width = Math.max(1, Math.floor(srcWidth * scale));
    const height = Math.max(1, Math.floor(srcHeight * scale));
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;

    ctx.drawImage(video, 0, 0, width, height);

    this.frameInFlight = true;
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', FRAME_JPEG_QUALITY),
    );
    if (!blob) {
      this.frameInFlight = false;
      return;
    }
    try {
      const buffer = await blob.arrayBuffer();
      const framed = new Uint8Array(5 + buffer.byteLength);
      framed[0] = SCREEN_FRAME_MESSAGE_TYPE;
      const view = new DataView(framed.buffer);
      view.setUint32(1, Math.floor(Date.now() / 1000) & 0xffffffff, true);
      framed.set(new Uint8Array(buffer), 5);
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(framed.buffer);
      }
    } catch (err) {
      console.warn('[ScreenShare] frame send failed:', err);
    } finally {
      this.frameInFlight = false;
    }
  }

  // ──────────── recording channel (MediaRecorder → binary 0x04) ────────────

  private startRecording(stream: MediaStream, ws: WebSocket): void {
    const mimeType = ScreenShareService.getSupportedMimeType();

    try {
      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: RECORDING_BITRATE,
      });
      this.actualMimeType = recorder.mimeType;
      this.recorder = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.sendRecordingChunk(event.data, ws).catch((err) => {
            console.warn('[ScreenShare] recording chunk send failed:', err);
          });
        }
      };

      recorder.onerror = (event) => {
        // MediaRecorder error events carry the underlying DOMException on
        // `.error`. The DOM lib types `ErrorEvent.error` as `any`, so narrow
        // to just the `.message` we read here.
        const eventError = (event as { error?: { message?: string } }).error;
        const error = new Error(eventError?.message || 'ScreenShare recording error');
        captureException(error, 'ScreenShareService.recorderError', {});
        this.config?.onError(error);
      };

      recorder.onstart = () => {
        // Report the screen recorder's capture-time anchor so the worker can
        // stamp the screen segment's client_start_ms. onstart fires before the
        // first chunk, so it reaches the worker ahead of the upload.
        // Best-effort: dropped if the socket is not open.
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'recording_started', kind: 'screen', client_start_ms: Date.now() }));
        }
      };

      recorder.start(RECORDING_CHUNK_INTERVAL_MS);
    } catch (err) {
      // Recording is best-effort - if it fails, still allow vision channel.
      console.warn('[ScreenShare] Failed to start recording:', err);
      captureException(err instanceof Error ? err : String(err), 'ScreenShareService.startRecording', {});
    }
  }

  private async sendRecordingChunk(blob: Blob, ws: WebSocket): Promise<void> {
    if (ws.readyState !== WebSocket.OPEN) return;
    const index = this.recordingChunkIndex++;
    const buffer = await blob.arrayBuffer();
    const mimeBytes = new TextEncoder().encode(this.actualMimeType);
    const headerSize = 6 + mimeBytes.length;
    const message = new Uint8Array(headerSize + buffer.byteLength);
    const view = new DataView(message.buffer);
    message[0] = SCREEN_VIDEO_MESSAGE_TYPE;
    view.setUint32(1, index, true);
    message[5] = mimeBytes.length;
    message.set(mimeBytes, 6);
    message.set(new Uint8Array(buffer), headerSize);
    ws.send(message.buffer);
  }

  private static getSupportedMimeType(): string | undefined {
    if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
      return undefined;
    }
    for (const mimeType of ScreenShareService.MIME_TYPES) {
      if (MediaRecorder.isTypeSupported(mimeType)) return mimeType;
    }
    return undefined;
  }
}

export function createScreenShareService(): ScreenShareService {
  return new ScreenShareService();
}
